import type { ScenarioModel } from '@/shared/generated'

import { find, pipe, prop } from 'lodash/fp'
import { useTranslation } from 'react-i18next'

import { usePricingCampaignContext } from '@/features/pricing-campaign/edit'

import { useSettingsSchemasQuery } from '@/entities/core'
import { useScenarioQuery } from '@/entities/scenario'

import { Alert, AlertTitle } from '@/shared/components/ui/alert'
import { LinkButton } from '@/shared/components/ui/link-button'
import { Skeleton } from '@/shared/components/ui/skeleton'
import { ampli } from '@/shared/lib/ampli'
import { useModals } from '@/shared/lib/modal-manager'
import {
	getEnumOptions,
	getResolvedSchema,
	translateOptions,
	translateSchemaKeys,
} from '@/shared/tools/json-schema-utils'

export function PricingTacticDemandFields() {
	const { t } = useTranslation()
	const { showModal } = useModals()
	const { isScenarioContext, scenarioId, optimizationGroupId, isGlobal } =
		usePricingCampaignContext()
	const settingsStrategy = useScenarioQuery<
		ScenarioModel['settings']['strategy']
	>(scenarioId!, {
		select: prop('settings.strategy'),
		refetchOnWindowFocus: 'always',
		enabled: !!scenarioId,
	})
	const schema = useSettingsSchemasQuery({
		select: pipe([
			getResolvedSchema('part:strategy'),
			prop('properties.demand_strategy'),
			(schema) => translateSchemaKeys(schema, t),
		]),
		refetchOnWindowFocus: 'always',
	})

	const targetOptions = translateOptions(
		getEnumOptions(prop('properties.target', schema.data)),
		t
	)
	const protectOptions = translateOptions(
		getEnumOptions(prop('properties.protect', schema.data)),
		t
	)

	const targetStrategyLabel =
		find(
			{ value: settingsStrategy.data?.demand_strategy?.target },
			targetOptions
		)?.label || '-'
	const protectStrategyLabel =
		find(
			{ value: settingsStrategy.data?.demand_strategy?.protect },
			protectOptions
		)?.label || '-'
	const label = targetStrategyLabel
	if (isGlobal) {
		return (
			<Alert variant='info'>
				<AlertTitle>
					{t('global.pc.settings.demand_strategy.message', {
						defaultValue:
							'Pricing strategy will be inherited from the Optimization group settings.',
					})}
				</AlertTitle>
			</Alert>
		)
	}

	return (
		<>
			<p className='mb-2 flex items-center gap-2 text-sm'>
				{!settingsStrategy.isLoading ? (
					t('app.grow_metric', {
						label,
						defaultValue: `Grow ${label}`,
					})
				) : (
					<Skeleton className='h-4 w-32 rounded-lg' />
				)}
				{!isScenarioContext && (
					<LinkButton
						variant='brand'
						onClick={() => {
							ampli.settingsPricingCampaignsDemandStrategyChangeClick()
							showModal('OPTIMIZATION_GROUP_MODAL', {
								optimizationGroupId: optimizationGroupId!,
							})
						}}
					>
						{t('app.change', {
							defaultValue: 'Change',
						})}
					</LinkButton>
				)}
			</p>

			<p className='mb-5 flex items-center gap-2 text-sm'>
				{!settingsStrategy.isLoading ? (
					t('app.maintain_metric', {
						protectStrategyLabel,
						defaultValue: `Maintain ${protectStrategyLabel}`,
					})
				) : (
					<Skeleton className='h-4 w-32 rounded-lg' />
				)}
				{!isScenarioContext && (
					<LinkButton
						variant='brand'
						onClick={() => {
							ampli.settingsPricingCampaignsDemandStrategyChangeClick()
							showModal('OPTIMIZATION_GROUP_MODAL', {
								optimizationGroupId: optimizationGroupId!,
							})
						}}
					>
						{t('app.change', {
							defaultValue: 'Change',
						})}
					</LinkButton>
				)}
			</p>
		</>
	)
}
